import {routerInstance} from '@aofl/router';
import routesConfig from '@modules/__config/routes';
import SessionActions from '@modules/session-sdo';
import {ApiService} from '@modules/api-service';
import {
  sdoNs,
  pages,
  rosterJobStatusPagesMap,
  dynamicPages,
  permissions,
  userTypes,
  localStorageKeys,
  dashboardTabs,
  apiNamespaces,
  landingPageProducts, thirdPartyRosterSystems,
} from '@modules/constants-enumerate';
import {storeInstance} from '@aofl/store/dist/legacy';
import {UserJobs} from '@modules/user-jobs';
import '@modules/sdo-user-info';
import '@modules/sdo-group-info';
import '@modules/sdo-classroom-info';
import '@modules/sdo-teacher-info';
import '@modules/sdo-student-info';
import '@modules/tabs/tabs-sdo';
import '@modules/sdo-resources';
import '@modules/sdo-parent-info';
import '@routes/parent/dashboard/sdo';
import '@routes/dashboard/sdo';
import {hasOneOfPermissions} from '@modules/permissions';
import {
  AnalyticEvent,
  AnalyticsTrackerQueue,
  ScrollListener,
  ClickListener,
  CurrentProduct,
} from '@epg/epg-components';
import {resourceEnumerateInstance} from '@modules/resource-enumerate-instance';
import {SearchParams} from '@modules/search-url-params';

ClickListener.init();
ScrollListener.init();

const routes = routesConfig.docs
  ? [...routesConfig.routes, ...routesConfig.docs]
  : routesConfig.routes;
if (window?.aofljsConfig?.__prerender__ === true) {
  // skip rotations
}
routerInstance.init(routes);

// check maintenance mode
routerInstance.before(async (request, response, next) => {
  await resourceEnumerateInstance.ready;
  const reData = await resourceEnumerateInstance.get(apiNamespaces.EMS);
  if (reData.payload.action.maintenance) {
    if (response.to.includes('/login/')) {
      return next(response); // allow login
    }

    if (request.from.includes('/503')) {
      return; // already on 503 page, cancel navigation away from it
    }

    if (request.to.includes('/503') === false) {
      return routerInstance.navigate(pages.ERROR_503, {});
    }
  }

  next(response);
});

// check logged in
routerInstance.before(async (request, response, next) => {
  const loggedIn = await SessionActions.validate();
  if (!loggedIn) {
    SessionActions.logout(false, false);
  }
  const inParentSection = response.to.includes('/parent');
  const newState = storeInstance.getState();
  if (inParentSection) {
    storeInstance.commit({
      namespace: sdoNs.LOCALE_DD,
      mutationId: 'setFilters',
      payload: ['en-US', 'es-US'],
    });
  } else {
    storeInstance.commit({
      namespace: sdoNs.LOCALE_DD,
      mutationId: 'setFilters',
      payload: [],
    });
    const storageLang = localStorage.getItem(localStorageKeys.LANG);
    const supportedLang = newState[sdoNs.LOCALE_DD].supportedLangs.find((item) => {
      return item.code === storageLang && item.enabled === true;
    });
    if (!supportedLang) {
      const defaultLang = newState[sdoNs.LOCALE_DD].supportedLangs[0].code;
      localStorage.setItem(localStorageKeys.LANG, defaultLang);
      document.documentElement.setAttribute('lang', defaultLang);
    }
  }

  next(response);
});

routerInstance.beforeEach(async (request, response, next) => {
  const state = storeInstance.getState();
  const isLoggedIn = state[sdoNs.SESSION].loggedIn;

  if (isLoggedIn && response.to.indexOf(pages.ASSIGN_SKILLS) === 0) {
    const url = new URL(location.href);
    const groupId = url.searchParams.get('group_id');

    if (groupId) {
      try {
        const groupInfo = await ApiService.groupGet([parseInt(groupId, 10)], ['id', 'name', 'path', 'type']);

        storeInstance.commit({
          namespace: sdoNs.SESSION,
          mutationId: 'setSelectedGroup',
          payload: groupInfo.results[groupId] || {}
        });
      } catch (error) {
        console.error('Error fetching group info (beforeEach):', error);
      }
    }
  }

  next(response);
});

routerInstance.before(async (request, response, next) => {
  if (response.to.indexOf(pages.LOGOUT) === 0) {
    await ApiService.logout();
    return routerInstance.navigate(pages.HOME);
  }

  next(response);
});

// add URL object to response
routerInstance.beforeEach((request, response, next) => {
  response.url = new URL(location.origin + response.to);
  next(response);
});

// redirect old search paths to new paths
routerInstance.beforeEach((request, response, next) => {
  if (response.matchedRoute) {
    return next(response);
  }

  const endString = response.url.pathname.endsWith('/') ? '' : '/';
  const matchedPath = response.url.pathname + endString;

  if (matchedPath === pages.STUDENT_SEARCH) {
    return routerInstance.navigate(pages.STUDENT_SEARCH_ACTIVE + response.url.search);
  }

  if (matchedPath === pages.CLASSES_SEARCH) {
    return routerInstance.navigate(pages.CLASSES_SEARCH_ACTIVE + response.url.search);
  }

  if (matchedPath === pages.TEACHER_SEARCH) {
    return routerInstance.navigate(pages.TEACHER_SEARCH_ACTIVE + response.url.search);
  }

  if (matchedPath.includes(pages.ORG_GROUP)) {
    return routerInstance.navigate(matchedPath + 'active' + response.url.search);
  }

  next(response);
});

// rerouting done here
routerInstance.beforeEach(async (request, response, next) => {
  const state = storeInstance.getState();
  const isLoggedIn = state[sdoNs.SESSION].loggedIn;
  const endString = response.to.endsWith('/') ? '' : '/';
  const matchedPath = response.to + endString;
  if (isLoggedIn) {
    const userType = state[sdoNs.SESSION].type;
    if (userType === userTypes.PARENT) {
      if (!matchedPath.includes('/parent') && !matchedPath.includes('/logout')) {
        return routerInstance.navigate(pages.PARENT_DASHBOARD);
      }

      // If user is logged in, but they're trying to go to /parent/redeem, send them to /parent/redeem-code
      if (matchedPath === pages.PARENT_REDEEM) {
        return routerInstance.navigate(pages.PARENT_REDEEM_CODE);
      }

      const isParentDashboard = matchedPath.includes(pages.PARENT_DASHBOARD);
      const isLookingAtStudentDashSection = (isParentDashboard && typeof response.matchedRoute?.props?.section !== 'undefined');

      let selectedStudent = {id: null, first_name: '', products: []};
      const studentResponse = await ApiService.parentGetStudents();
      // If going to /parent/dashboard/<student_id>
      if (response.matchedRoute?.props?.studentId) {
        const studentId = parseInt(response.matchedRoute.props.studentId, 10);
        studentResponse.students.map((student) => {
          if (student.id === studentId) {
            selectedStudent = student;
          }
        });
      } else {
        selectedStudent = studentResponse.students[0];
      }
      storeInstance.commit(
        {namespace: sdoNs.PARENT_DASHBOARD, mutationId: 'setStudents', payload: studentResponse.students},
        {namespace: sdoNs.PARENT_DASHBOARD, mutationId: 'setSelectedStudent', payload: selectedStudent},
      );
      // Handle case if user tries to just go to: /parent/dashboard
      if ((!response.matchedRoute?.props?.studentId || !response.matchedRoute) && isParentDashboard) {
        return routerInstance.navigate(dynamicPages.parentDashboard(selectedStudent.id));
      }

      storeInstance.commit({
        namespace: sdoNs.SITE_HEADER,
        mutationId: 'setBackBtnUrl',
        payload: (isLookingAtStudentDashSection) ? dynamicPages.parentDashboard(selectedStudent.id) : null
      },
      {namespace: sdoNs.SITE_HEADER, mutationId: 'setTitle', payload: (isLookingAtStudentDashSection) ? selectedStudent.first_name : ''});
    } else {
      if (matchedPath.includes('/password')) {
        routerInstance.navigate(pages.DASHBOARD, {});
        return;
      } else if (matchedPath.includes('/parent')) {
        return routerInstance.navigate(pages.DASHBOARD, {});
      } else if (matchedPath === pages.HOME) {
        const url = new URL(location.href);
        const defaultPage = state[sdoNs.SESSION].$routeToLanding ? pages.LANDING : pages.DASHBOARD;
        const toUrl = decodeURIComponent(url.searchParams.get('to') || defaultPage);
        return routerInstance.navigate(toUrl, {forceReload: true});
      }
    }
  } else {
    if (!response?.matchedRoute?.path) {
      next(response);
      return;
    }

    const skipAuth = response?.matchedRoute?.meta?.skipAuth;
    if (skipAuth) {
      next(response);
      return;
    }

    const reroutePrefix = pages.HOME + '?to=';
    if (matchedPath !== pages.HOME && !matchedPath.startsWith(reroutePrefix)) {
      response.to = reroutePrefix + encodeURIComponent(response.to);
    }
  }

  next(response);
});

routerInstance.beforeEach((request, response, next) => {
  const state = storeInstance.getState();
  const isLoggedIn = state[sdoNs.SESSION].loggedIn;

  if (isLoggedIn) {
    const url = new URL(location.href);
    const productInUrl = decodeURIComponent(url.searchParams.get('product'));
    const state = storeInstance.getState();
    const availableProducts = state[sdoNs.SESSION].$activeAoflProducts;
    const product = CurrentProduct.get(availableProducts[0]);
    const currentProduct = availableProducts.includes(product) ? product : availableProducts[0];
    if (product === '') {
      CurrentProduct.set(currentProduct);
    }
    storeInstance.commit(
      {namespace: sdoNs.DASHBOARD, mutationId: 'setDashboardGroupInfo', payload: {}},
    );

    // We're on dashboard group page
    // response.meta we are visiting from dashboard group modal
    // response.to.indexOf is for when we refresh the page
    if (response.meta.dashboardGroupId !== undefined || response.to.indexOf('dashboardGroupId') !== -1) {
      const dashboardGroupId = response.meta.dashboardGroupId || url.searchParams.get('dashboardGroupId');
      // Currently we only allow new mma and mra
      const allowedProducts = availableProducts.filter((value) => landingPageProducts.includes(value));
      if (!allowedProducts.some((value) => productInUrl === value)) {
        url.searchParams.set('product', allowedProducts[0]);
        CurrentProduct.set(allowedProducts[0]);
      }
      const dashboardGroupType = url.searchParams.get('dashboardGroupType') || response.meta.dashboardGroupType;
      storeInstance.commit(
        {namespace: sdoNs.DASHBOARD,
          mutationId: 'setDashboardGroupInfo',
          payload: {id: dashboardGroupId, type: dashboardGroupType}},
      );
      url.searchParams.set('dashboardGroupId', dashboardGroupId);
      url.searchParams.set('dashboardGroupType', dashboardGroupType);
      response.to = response.url.pathname + url.search;
    } else if (
      (response.to.indexOf('product') === -1 || !productInUrl || availableProducts.indexOf(productInUrl) === -1)
      && !url.searchParams.has('to')
    ) {
      // Need this for when we visit dashboard page from left menu or any other pages.DASHBOARD
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('product', currentProduct);
      const url = response.url.pathname + '?' + searchParams.toString();
      history.pushState({}, '', url); // when pressing back, this logic does not get run and url does not have product query without this
      response.to = url;
    }
    // Lets set group id to local storage here
    const searchParams = SearchParams.decode(url.searchParams);
    let groupId;
    if (Array.isArray(searchParams.filters?.stats_path)) {
      if (searchParams.filters?.stats_path[0].charAt(searchParams.filters?.stats_path[0].length - 1) !== '.') {
        searchParams.filters.stats_path[0] += '.';
      }
      const path = searchParams.filters?.stats_path[0].split('.');
      path.pop();
      if (path.length >= 1) {
        groupId = parseInt(path[path.length - 1], 10);
      } else {
        groupId = state[sdoNs.SESSION].current_role.group_id;
      }
    } else {
      groupId = state[sdoNs.SESSION].current_role.group_id;
    }
    localStorage.setItem('group_id', groupId);
  }
  next(response);
});

routerInstance.beforeEach(async (request, response, next) => {
  // call endpoint to validate code if parent is redeeming
  const isParentWelcomePage = response.to.indexOf(pages.PARENT_WELCOME) === 0;
  if (isParentWelcomePage) {
    const code = response.matchedRoute.props.code;
    try {
      const payload = await ApiService.userVerifyActivationCode(code);
      storeInstance.commit(
        {namespace: sdoNs.PARENT_INFO, mutationId: 'setProductsForCode', payload: payload.products || []},
        {namespace: sdoNs.PARENT_INFO, mutationId: 'setActivationCode', payload: code},
      );
    } catch (e) {
      response.to = pages.PARENT_REDEEM + '?code=' + code;
      console.error(e); // eslint-disable-line no-console
    }
  }

  next(response);
});

// Add Page view event to AnalyticQueue
routerInstance.beforeEach((request, response, next) => {
  const event = AnalyticEvent.create('page.view', {
    page_title: response.matchedRoute?.title,
    uri: response.url.href || response.to,
  });
  AnalyticsTrackerQueue.enqueueEvent(event);
  next(response);
});

// check permissions
routerInstance.beforeEach((request, response, next) => {
  const allowedPermissions = response?.matchedRoute?.meta?.allowedPermissions;
  if (allowedPermissions) {
    const allowed = allowedPermissions.map((item) => permissions[item]);
    if (!hasOneOfPermissions(allowed)) {
      response.matchedRoute = null;
    }
  }
  next(response);
});

// get token
routerInstance.beforeEach(async (request, response, next) => {
  if (response.matchedRoute === null) return next(response);
  if (response.to.indexOf(pages.TOKEN_LOGIN) === 0) {
    const {token} = response.matchedRoute.props;
    storeInstance.commit({
      namespace: sdoNs.SESSION,
      mutationId: 'setLoginToken',
      payload: token
    });
  }

  next(response);
});

// fetch user profile
routerInstance.beforeEach(async (request, response, next) => {
  const myProfilePage = response.to.indexOf(pages.MY_PROFILE) === 0;
  if (response.to.indexOf(pages.ADMIN_PROFILE) === 0 || myProfilePage) {
    try {
      const state = storeInstance.getState();
      const currentUserId = state[sdoNs.SESSION].id;
      let id = parseInt(response.matchedRoute.props.userId, 10);
      if (!myProfilePage && id === currentUserId) {
        response.to = pages.MY_PROFILE;
      } else {
        if (myProfilePage) {
          id = currentUserId;
        }

        const payload = await ApiService.userGet(id, state[sdoNs.SESSION].current_profile.type);
        storeInstance.commit({namespace: sdoNs.ADMIN_UINFO, mutationId: 'setInfo', payload});
      }
    } catch (e) {
      response.matchedRoute = null;
      console.error(e); // eslint-disable-line no-console
    }
  }
  next(response);
});

// fetch teacher info
routerInstance.beforeEach(async (request, response, next) => {
  if (response.matchedRoute === null) return next(response);
  const {id} = response.matchedRoute.props;
  if (response.to.indexOf(dynamicPages.teacherProfile(id)) !== -1) {
    try {
      const payload = await ApiService.teacherGet(parseInt(id, 10));
      storeInstance.commit({namespace: sdoNs.TEACHER_INFO, mutationId: 'setData', payload});
    } catch (e) {
      response.matchedRoute = null;
      console.error(e); // eslint-disable-line no-console
    }
  }
  next(response);
});

// fetch student info
routerInstance.beforeEach(async (request, response, next) => {
  if (response.matchedRoute === null) return next(response);
  if (response.to.indexOf(pages.STUDENT_SEARCH) !== -1) { // any student page
    storeInstance.commit({
      namespace: sdoNs.STUDENT_INFO,
      mutationId: 'init'
    });
  }
  next(response);
});

// fetch resource info
routerInstance.beforeEach(async (request, response, next) => {
  if (response.matchedRoute === null) return next(response);
  if (response.to.indexOf(pages.RESOURCES) !== -1) {
    const state = storeInstance.getState();
    // If resources are not enabled, redirect to home page
    const userType = state[sdoNs.SESSION].type;
    if (state[sdoNs.SESSION].settings.hide_sections[userType]?.resources) {
      response.to = pages.HOME;
    }
  }
  next(response);
});

// rosters routing rules
routerInstance.beforeEach(async (request, response, next) => {
  const state = storeInstance.getState();
  const isLoggedIn = state[sdoNs.SESSION].loggedIn;

  const tpGroup = state[sdoNs.SESSION].third_party_connected_group;
  const thirdPartyEnabled = tpGroup && tpGroup.third_party_rostering_system !== 'frogstreet' && tpGroup.third_party_rostering_system !== thirdPartyRosterSystems.aofl_rostering_solution;
  const allowManualEntries = state[sdoNs.SESSION].settings.allow_manual_entries || false;

  const page = response.to.split('?')[0].replace(/\/?$/, '/');
  const isRosteringPage = page.indexOf(pages.ROSTERS) === 0;

  if (isLoggedIn && isRosteringPage) {
    try {
      const isUploadFullPage = page.indexOf(pages.ROSTERS_UPLOAD_FULL) === 0;
      const isUploadNewPage = page.indexOf(pages.ROSTERS_UPLOAD_NEW) === 0;
      const isUploadOneRoster = page.indexOf(pages.ROSTERS_UPLOAD_ONEROSTER) === 0;
      const isUploadPage = isUploadFullPage || isUploadNewPage || isUploadOneRoster;
      const lowerLevelAdminsPermissions = [
        permissions.ROSTER_FULL,
        permissions.ROSTER,
        permissions.EXPORT_ALL,
        permissions.ALL,
      ];
      if (
        (!hasOneOfPermissions([permissions.ROSTER]) && isUploadNewPage)
        ||
        (!hasOneOfPermissions(lowerLevelAdminsPermissions) && isUploadFullPage)
      ) {
        response.to = pages.ROSTERS_ARCHIVE;
        throw new Error('Permission Denied');
      }

      const rosterJobs = await ApiService.rosterJobGetList();
      storeInstance.commit({namespace: sdoNs.USER_JOBS, mutationId: 'setRosterJobs', payload: rosterJobs.jobs});
      const jobsState = storeInstance.getState()[sdoNs.USER_JOBS];

      const isMainRosterPage = page === pages.ROSTERS;
      const hasRosterJobs = jobsState.rosterJobs.length > 0;
      const hasRoster = rosterJobs.has_roster || false;
      const hasActiveRosterJob = typeof jobsState.$activeRosterJob.status !== 'undefined';

      if (!thirdPartyEnabled) {
        // if (hasPermission([permissions.ROSTER_FULL]) && !hasRosterJobs && !isUploadPage && !hasRoster) {
        //   response.to = pages.ROSTERS_UPLOAD_FULL;
        // } else if (hasPermission([permissions.ROSTER]) && !hasRosterJobs && !isUploadPage && !hasRoster) {
        //   response.to = pages.ROSTERS_UPLOAD_NEW;
        // } else
        if (!hasRosterJobs && isMainRosterPage) {
          response.to = pages.ROSTERS_ARCHIVE;
        } else if (UserJobs.isDone(jobsState.$activeRosterJob) && !isMainRosterPage && !isUploadPage || (isMainRosterPage && hasRoster)) {
          response.to = pages.ROSTERS_ARCHIVE;
        } else if (
          hasActiveRosterJob && !isUploadPage &&
          response.to !== rosterJobStatusPagesMap[jobsState.$activeRosterJob.status]
        ) {
          response.to = rosterJobStatusPagesMap[jobsState.$activeRosterJob.status];
        }

      } else if (thirdPartyEnabled && allowManualEntries) {
        // do nothing
      } else if (!isMainRosterPage) {
        response.to = pages.ROSTERS;
      }
    } catch (e) {
      response.to = pages.ERROR_500;
      console.error(e); // eslint-disable-line no-console
    }
  }
  next(response);
});

// validate org-group level
routerInstance.beforeEach((request, response, next) => {
  const state = storeInstance.getState();
  const level = response.matchedRoute?.props?.level;

  if (response.to.indexOf(pages.ORG_GROUP) === 0 && (typeof level === 'undefined' || typeof state[sdoNs.SESSION].$myGroups[parseInt(level, 10)] === 'undefined')) {
    response.matchedRoute = null;
  }
  next(response);
});

routerInstance.after((request, response, next) => {
  // hack, required by Appcues.page(); copied from @aofl/router/modules/Router/index.js
  if (!request.meta.poppedState) {
    if (request.meta.replaceState) {
      window.history.replaceState(null, null, response.to);
    } else {
      window.history.pushState(null, null, response.to);
      request.meta.replaceState = true; // see Router/index.js
    }
  }

  window.Appcues && window.Appcues.page();
  next(response);
});

// This is to handle the case for teacher dash reports tab.
// set state from url
routerInstance.after((request, response, next) => {
  const isDashboard = (response.matchedRoute?.path + '/') === pages.DASHBOARD;
  if (isDashboard) {
    const url = new URL(location.href);
    let tabInUrl = decodeURIComponent(url.searchParams.get('tab'));
    const tabs = {
      [dashboardTabs.PROGRESS]: false,
      [dashboardTabs.TRACKER]: false,
    };
    const isValidTab = Object.keys(tabs)?.indexOf(tabInUrl) >= 0;
    // if nothing passed in url or something is passed, but it doesnt
    // match one of the options, default to snapshot.
    if (!isValidTab) {
      tabInUrl = dashboardTabs.TRACKER;
    }
    tabs[tabInUrl] = true;
    storeInstance.commit(
      {
        namespace: sdoNs.TABS,
        mutationId: 'addGroup',
        payload: {
          groupId: 'teacher-dash',
          tabs
        }
      },
    );
  }
  next(response);
});

// set zendesk info
routerInstance.after((request, response, next) => {
  const state = storeInstance.getState();
  const session = state[sdoNs.SESSION];

  if (session.loggedIn && window.zE) {
    window.zE('webWidget', 'updateSettings', {
      webWidget: {
        contactForm: {
          tags: [`tier-${session.group_info.level}`],
        }
      }
    });

    window.zE('webWidget', 'prefill', {
      name: {
        value: session.name
      },
      email: {
        value: session.email
      },
      phone: {
        value: session.phone
      }
    });
  }
  next(response);
});

routerInstance.navigate(
  location.href.replace(location.origin, '').replace('index.html', ''),
  {forceReload: true}
);
